/* eslint-disable jsx-a11y/anchor-is-valid */
import { round } from 'lodash';
import { ParsedQuery } from 'query-string';
import React, { ReactNode } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { formatter } from 'components/atoms/input-duration';
import { useHash } from 'hooks/use-hash';
import { useSearch } from 'hooks/use-search';
import { TemplateInsightSubTask, TemplateInsightViews } from './constant';

const BaseWrapper = styled.div`
  background: var(--coolest-gray-10);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Wrapper = styled(BaseWrapper)`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 var(--spacing-05);
  padding-left: var(--left-spacing);
  margin-bottom: var(--spacing-06);
  gap: var(--spacing-05);
  .ant-table-wrapper {
    overflow: auto;
  }
`;

export const OuterWrapper = styled(BaseWrapper)``;

export const SubtabWrapper = styled.div`
  padding: var(--spacing-05);
  padding-left: var(--left-spacing);
`;

export const ChartTextStyle = {
  style: {
    fontFamily: 'var(--ozn-font-sans)',
    fontWeight: 'var(--heading-4-weight)',
    fontSize: 'var(--font-size-base)',
    letterSpacing: '0.16px',
    color: 'var(--grey-100)',
  },
};

const ColoredString = styled.p<{ cmpValue: number }>`
  color: ${(props) =>
    props.cmpValue > 0
      ? 'var(--green-50)'
      : props.cmpValue < 0
      ? 'var(--red-50)'
      : 'inherit'};
  margin: 0;
`;

const StyledButton = styled(Button)`
  padding: 0;
  height: var(--height-base);
  width: 100%;
  * {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const renderAsString = ({
  text,
  cmpValue,
}: {
  text: ReactNode;
  cmpValue: number;
}): JSX.Element => {
  return <ColoredString cmpValue={cmpValue}>{text}</ColoredString>;
};

const getValue = (
  obj: ParsedQuery<string>,
  key: string,
  fallback = '',
): string => {
  const value = obj[key];

  return Array.isArray(value) ? value[0] : value != null ? value : fallback;
};

export const renderAsTemplateLink = (name, { template }) => {
  const Renderer = ({ templateName, templateId: id }) => {
    const { push } = useHistory();
    const [_hash, _, getNextHash] = useHash({
      from: (value) => value,
      to: (value) => value,
    });

    const [search, _setSearch, getNextSearch] = useSearch<{
      subTab: string;
      criteria: string | string[];
    }>({
      from: (value) => {
        const subTab = getValue(value, 'subTab');
        const criteria = value.criteria || '';

        return {
          subTab,
          criteria,
        };
      },
      to: (value) => value,
    });

    const overviewTabIndex = TemplateInsightSubTask[
      TemplateInsightViews.Overview
    ].findIndex((overview) => overview === search.subTab);

    const filteredCriteria = Array.isArray(search.criteria)
      ? search.criteria.filter((x) => !x.includes('template'))
      : search.criteria.includes('template')
      ? ''
      : search.criteria;

    return (
      <StyledButton
        type="link"
        onClick={() => {
          push({
            search: getNextSearch({
              criteria: filteredCriteria.concat(`template.is.${id}`),
              subTab:
                TemplateInsightSubTask[TemplateInsightViews.ByTemplate][
                  overviewTabIndex
                ],
            }),
            hash: getNextHash({ view: TemplateInsightViews.ByTemplate }),
          });
        }}
      >
        {templateName}
      </StyledButton>
    );
  };

  return (
    <Renderer
      templateId={template.templateId}
      templateName={template.templateName}
    />
  );
};

export const renderAsDays = (fieldValue?: number): JSX.Element => (
  <p>{fieldValue !== null ? `${fieldValue}d` : '-'}</p>
);
export const renderAsPercentage = (fieldValue: number): JSX.Element => (
  <p>{fieldValue !== null ? `${round(fieldValue)}%` : '-'}</p>
);

export const renderAsFormattedTime = (fieldValue: string): JSX.Element => (
  <p>{fieldValue !== null ? formatter(fieldValue) : '-'}</p>
);

export const renderAsCompare = ({
  value,
  cmpValue,
}: {
  value: number;
  cmpValue: number;
}): JSX.Element => {
  return renderAsString({ text: `${value}d`, cmpValue });
};

export const renderAsBillable = ({
  value,
  percentage,
}: {
  value: number;
  percentage: number;
}): JSX.Element => {
  return (
    <p>
      {percentage != null ? `${round(percentage)}% (${formatter(value)})` : '-'}
    </p>
  );
};

export enum TableFieldTypes {
  String = 'string',
  Number = 'number',
  Days = 'days',
  Percentage = 'percentage',
  Time = 'time',
  CompareValue = 'compareValue',
  TimeWithPercentage = 'timeWithPercentage',
}

export const getTableCellRender = ({
  fieldName,
  fieldType = '',
}: {
  fieldName: string;
  fieldType?: string;
}) => {
  const rendererByName = { templateName: renderAsTemplateLink };
  const rendererByType = {
    [TableFieldTypes.Days]: renderAsDays,
    [TableFieldTypes.Percentage]: renderAsPercentage,
    [TableFieldTypes.Time]: renderAsFormattedTime,
    [TableFieldTypes.CompareValue]: renderAsCompare,
    [TableFieldTypes.TimeWithPercentage]: renderAsBillable,
  };

  return rendererByName[fieldName] || rendererByType[fieldType] || undefined;
};
